<script setup lang="ts">
import {computed, ref, nextTick, onMounted, onUnmounted, watch} from 'vue';
import {DELETE, GET, PATCH, POST} from '../modules/fetch.ts';
import {confirmModal} from '../features/comp/ConfirmModal.ts';
import {renderPreviewPanelContent} from '../features/repo-editor.ts';
import {SvgIcon} from '../svg.ts';
import DrcyCsvTable from './DrcyCsvTable.vue';
import DrcyCellResolver from './DrcyCellResolver.vue';
import DrcySheetWorkspace from './DrcySheetWorkspace.vue';
import {parseCsv, serializeCsv, setCsvCell} from '../utils/csv.ts';
import {downloadTextFile} from '../utils/download.ts';
import {joinNotesColumn, parseCellNotes, type ResolveTarget} from '../utils/drcyCellNotes.ts';

type ChatRole = 'user' | 'assistant';
type ActivityKind = 'tool' | 'status';

type ChatMessage = {
  id: number;
  turnId: number;
  role: ChatRole;
  status: string;
  content: string;
  // Transient activity, live from SSE, not persisted: a status or the tool DRCY
  // is currently running. Only tools count toward the end-of-turn summary.
  activity: string;
  activityKind: ActivityKind;
  toolCount: number;
  rating: ChatRating;
  ratingNote: string;
  // Whether the note box is open for this answer. A thumbs-down opens it
  // itself; a thumbs-up offers it and waits to be asked.
  noteOpen: boolean;
  noteDraft: string;
  noteError: string;
};

type ChatRating = '' | 'up' | 'down';

// Wire shape from the Hub (api.DrcyChatMessageEntry) and SSE event payloads.
type MessageEntry = {
  id?: number;
  turn_id?: number;
  role: ChatRole;
  status?: string;
  content?: string;
  rating?: ChatRating;
  rating_note?: string;
};

type ChatArtifact = {
  id: number;
  turn_id: number;
  title: string;
  filename: string;
  format: 'markdown' | 'csv';
  media_type: string;
  content: string;
  revision: number;
  // 'committing' is a transient backend state during a commit; the list endpoint
  // filters it out, but include it so the type matches every value the API can send.
  state: 'draft' | 'committing' | 'committed';
  source_ref?: string;
  source_commit_sha?: string;
  // Per-cell provenance the worker published beside a CSV sheet; opaque here,
  // read by the table for its hover bubbles.
  cell_notes?: unknown;
};

// Wire shape from the Hub (api.DrcyChatFile): a file the user uploaded to the
// session. Metadata only; content is never fetched into the chat window.
type ChatFile = {
  id: number;
  turn_id: number;
  filename: string;
  extension: string;
  size: number;
  media_type: string;
  revision: number;
  sha256: string;
  updated_at: string;
};

type DesignChangeValidation = {
  target?: string;
  records_changed?: number;
  changes?: Array<{target?: string; property: string; before?: string | null; after: string}>;
  checks?: string[];
  warnings?: string[];
};

type DesignChange = {
  id: number;
  turn_id: number;
  title: string;
  path: string;
  operation: Record<string, unknown>;
  validation: DesignChangeValidation;
  source_ref: string;
  source_commit_sha: string;
  source_sha256: string;
  proposed_sha256: string;
  size: number;
  revision: number;
  state: 'draft' | 'applying' | 'applied';
};

const props = withDefaults(
  defineProps<{
    // Chat scope: "pr:<index>" (a design review) or "repo:<branch>".
    scope: string;
    repoLink: string;
    defaultBranch: string;
    // Branch a committed artifact targets by default: the design review's head
    // branch for a pr: scope, or the branch for a repo: scope. Falls back to
    // defaultBranch when empty.
    commitBranch?: string;
    drcyAvatar: string;
    thinkingSrc?: string;
    queuedLabel?: string;
    queuedBehindLabel?: string;
    startingLabel?: string;
    stopLabel?: string;
    stopConfirmTitle?: string;
    stopConfirmMessage?: string;
    stopErrorLabel?: string;
    userName?: string;
    userAvatar?: string;
    userHomeLink?: string;
    // A conversation entry point supplies its own focused empty state and
    // starters in the page template, so do not repeat the generic onboarding.
    contextual?: boolean;
    resume?: boolean;
    resumeContextKey?: string;
    contextCommentId?: string;
    contextIssueIndex?: string;
    contextAllReviews?: string;
  }>(),
  {
    stopLabel: 'Stop', stopConfirmTitle: 'Stop this response?', stopConfirmMessage: 'Your chat and saved drafts will be kept. Continuing later starts a new run.', stopErrorLabel: 'Could not stop this response. Please try again.', queuedLabel: 'Queued. Waiting for an available runner…', queuedBehindLabel: 'Queued behind another response…', startingLabel: 'Spinning up an agent runner…', thinkingSrc: '', userName: 'You', userAvatar: '', userHomeLink: '', commitBranch: '',
    resume: false, resumeContextKey: '', contextual: false, contextCommentId: '', contextIssueIndex: '', contextAllReviews: '',
  },
);

const stoppingTurn = ref<number | null>(null);
const stopError = ref('');
const stopErrorTurn = ref<number | null>(null);
const messages = ref<ChatMessage[]>([]);
const artifacts = ref<ChatArtifact[]>([]);
const designChanges = ref<DesignChange[]>([]);
const files = ref<ChatFile[]>([]);
// An uploaded CSV opened as a read-only table. Fetched only when the user asks,
// so attaching a file never pulls its bytes into the chat window by itself.
const previewFileId = ref<number | null>(null);
const previewFileContent = ref('');
const previewFileError = ref('');
const previewFileLoading = ref(false);
// The composer lives in the page template; the chip row of attached files
// teleports to a slot above it that only exists when the composer does.
const fileChipTarget = ref<string | null>(null);
const responseControlsTarget = ref<string | null>(null);
const activeResponse = computed(() => messages.value.find((message) => message.role === 'assistant' && (message.status === 'pending' || message.status === 'running')));
const activeArtifactId = ref<number | null>(null);
const activeChangeId = ref<number | null>(null);
const failedChangePreviews = ref<Set<number>>(new Set());
const artifactSidebarOpen = ref(false);
// The sheet workspace: one CSV artifact expanded over the whole page so the
// engineer can work a wide sheet with room, then minimize back to the chat.
const expandedArtifactId = ref<number | null>(null);
const expandedArtifact = computed(() => artifacts.value.find((artifact) => artifact.id === expandedArtifactId.value) ?? null);
// The cell resolver: one panel for whichever flagged sheet cell the engineer
// opened, in the inline card or the workspace, on the revision it was opened
// from.
type OpenResolver = {artifactId: number; revision: number; target: ResolveTarget};
const resolver = ref<OpenResolver | null>(null);
const editingArtifactId = ref<number | null>(null);
const editTitle = ref('');
const editFilename = ref('');
const editContent = ref('');
// Captured when editing begins so Save uses the revision the user started from,
// not a newer one an SSE refresh may have swapped into the artifact object;
// that keeps optimistic locking honest (a concurrent edit yields a 409).
const editRevision = ref<number>(0);
const artifactError = ref('');
// A success notice naming the branch a commit landed on, with a link to it. The
// commit can fall back to an auto-named branch (e.g. when the target is
// protected), so report where it actually went rather than navigating away.
const artifactNotice = ref('');
const artifactNoticeUrl = ref('');
const committing = ref(false);
const applyingChange = ref(false);
const scroller = ref<HTMLElement>();

let eventSource: EventSource | null = null;

// Fallback poll while a turn is in flight. SSE is the primary channel, but a
// silently-dead-but-"open" stream (server restart, proxy timeout, sleep/wake)
// never fires 'open'/'error', so the reconnect catch-up can't run and the turn
// would spin forever. Polling history reconciles content and terminal status
// regardless of the stream's health, then stops once nothing is in flight.
let pollTimer: number | null = null;
const POLL_INTERVAL_MS = 3000;

// Last emitted busy state, so we only notify the composer on transitions.
let lastBusy = false;

// A turn stays 'pending' until a worker claims it, and the thinking animation
// alone reads as work already underway. The notice waits before appearing so a
// normal claim passes unremarked and only a queue the reader would otherwise
// mistake for a slow answer is named.
const QUEUED_NOTICE_DELAY_MS = 4000;
const queuedNoticeVisible = ref(false);
let queuedNoticeTimer: number | null = null;
// What the send told us about the turn it created: whether it started a worker,
// and whether another turn was already being answered. Only a turn sent from
// this window is in here, so one found in history (after a reload) is not.
type SendState = {dispatched: boolean; queuedBehind: boolean};
const sentTurns = ref(new Map<number, SendState>());
// Turns a runner has taken, from the 'queue' stream event. The job going
// running is the one hard sign of progress before the worker claims a turn.
const startingTurns = ref(new Set<number>());

watch(
  () => activeResponse.value?.status === 'pending' && !activeResponse.value.activity,
  (queued) => {
    if (queuedNoticeTimer !== null) {
      clearTimeout(queuedNoticeTimer);
      queuedNoticeTimer = null;
    }
    if (!queued) {
      queuedNoticeVisible.value = false;
      return;
    }
    queuedNoticeTimer = window.setTimeout(() => {
      queuedNoticeVisible.value = true;
      queuedNoticeTimer = null;
    }, QUEUED_NOTICE_DELAY_MS);
  },
  {immediate: true},
);

// Which wait a pending turn is in, most definite signal first. A send that
// started a worker is waiting on a runner whatever else is on the scope: an
// abandoned turn can still read as running once its lease has expired, which
// would otherwise claim the new turn is queued behind it.
function queuedLabelFor(message: ChatMessage): string {
  if (startingTurns.value.has(message.turnId)) return props.startingLabel;
  const sent = sentTurns.value.get(message.turnId);
  if (sent?.dispatched) return props.queuedLabel;
  return sent?.queuedBehind ? props.queuedBehindLabel : props.queuedLabel;
}

// What to say under the animation while a turn waits, or '' to say nothing yet.
//
// A send that started a worker is named at once: a job is queued and a runner
// has to take it, which is the wait people have been reading as a slow model.
// Every other wait gets the delay first, because a running worker usually
// claims within a poll and a notice on every turn would be noise.
function queuedNotice(message: ChatMessage): string {
  if (message.status !== 'pending' || message.activity) return '';
  if (startingTurns.value.has(message.turnId)) return queuedLabelFor(message);
  if (sentTurns.value.get(message.turnId)?.dispatched) return queuedLabelFor(message);
  return queuedNoticeVisible.value ? queuedLabelFor(message) : '';
}

// The one spoken copy of what DRCY is doing, for the live region below. The
// animation is decorative, so nothing here is announced twice: a queued turn
// says which wait it is in, a working one names its current tool, and a turn
// with neither still says something rather than going silent.
function statusLine(message: ChatMessage): string {
  if (message.role !== 'assistant') return '';
  if (message.status !== 'pending' && message.status !== 'running') return '';
  const queued = queuedNotice(message);
  if (queued) return queued;
  if (message.activity) {
    return message.activityKind === 'tool' ? `Tool: ${message.activity}` : message.activity;
  }
  return 'DRCY is thinking…';
}

// History requests can overlap once polling and a resume run together, and
// only the newest response may replace what is on screen. Counting what has
// been applied as well as what has been issued keeps an older success usable
// when a newer request failed outright.
let historyRequests = 0;
let historyApplied = 0;

// Message bodies we render markdown into, keyed by message id, plus per-message
// debounce timers so streaming re-renders don't hammer the server.
const bubbleEls = new Map<number, HTMLElement>();
const renderTimers = new Map<number, number>();
const RENDER_DEBOUNCE_MS = 350;
// Content most recently rendered (or dispatched to /markup) per message id, so
// we skip re-rendering unchanged content (e.g. every 3s poll re-upserts every
// message) and can discard a stale /markup response.
const renderedContent = new Map<number, string>();

const baseUrl = `${props.repoLink}/drcy_chat`;
const scopeQuery = `scope=${encodeURIComponent(props.scope)}`;
// The conversation this chat was entered from, when it was entered from one.
const contextParams: Record<string, string> = {};
if (props.contextCommentId) {
  contextParams.context_comment_id = props.contextCommentId;
  contextParams.context_all_reviews = String(props.contextAllReviews === 'true');
} else if (props.contextIssueIndex) {
  contextParams.context_issue_index = props.contextIssueIndex;
}
const hasConversationContext = Object.keys(contextParams).length > 0;

function authorName(message: ChatMessage): string {
  return message.role === 'assistant' ? 'DRCY' : props.userName;
}
function avatarSrc(message: ChatMessage): string {
  return message.role === 'assistant' ? props.drcyAvatar : props.userAvatar;
}
function avatarHref(message: ChatMessage): string | undefined {
  return message.role === 'assistant' ? undefined : props.userHomeLink || undefined;
}

// The rating is saved before the note is touched, so an abandoned note box
// costs nothing: the verdict is the signal, the note is the lead.
async function rate(message: ChatMessage, rating: ChatRating) {
  const next = message.rating === rating ? '' : rating;
  const previous = {rating: message.rating, note: message.ratingNote};
  message.rating = next;
  if (!next) {
    message.ratingNote = '';
    message.noteOpen = false;
  } else {
    // The verdict is already saved, so the box asks rather than gates: it can
    // be skipped and the rating stands.
    message.noteOpen = true;
    message.noteDraft = message.ratingNote;
    message.noteError = '';
  }
  if (!(await saveRating(message, next, next ? message.ratingNote : ''))) {
    message.rating = previous.rating;
    message.ratingNote = previous.note;
    message.noteOpen = false;
  }
}

async function saveRatingNote(message: ChatMessage) {
  const note = message.noteDraft.trim();
  if (!(await saveRating(message, message.rating, note))) {
    // A refused note stays in the box with a reason. Closing it would throw
    // away what the reader wrote and leave the rating looking noted.
    message.noteError = 'That note could not be saved. Try shortening it.';
    return;
  }
  message.ratingNote = note;
  message.noteError = '';
  message.noteOpen = false;
}

async function saveRating(message: ChatMessage, rating: ChatRating, note: string) {
  const data = new FormData();
  data.append('rating', rating);
  data.append('note', note);
  try {
    const res = await POST(`${baseUrl}/turns/${message.turnId}/rating?${scopeQuery}`, {data});
    return res.ok;
  } catch {
    return false;
  }
}

function upsert(entry: MessageEntry) {
  if (!entry.id) return;
  const existing = messages.value.find((m) => m.id === entry.id);
  // A history response can predate Stop even when it arrives after its SSE.
  if (existing?.role === 'assistant' && existing.status === 'cancelled' && entry.status !== 'cancelled') return;
  const next: ChatMessage = {
    id: entry.id,
    turnId: entry.turn_id ?? 0,
    role: entry.role,
    status: entry.status ?? '',
    content: entry.content ?? '',
    activity: existing?.activity ?? '',
    activityKind: existing?.activityKind ?? 'status',
    toolCount: existing?.toolCount ?? 0,
    rating: entry.rating ?? existing?.rating ?? '',
    ratingNote: entry.rating_note ?? existing?.ratingNote ?? '',
    noteOpen: existing?.noteOpen ?? false,
    noteDraft: existing?.noteDraft ?? '',
    noteError: existing?.noteError ?? '',
  };
  const index = messages.value.findIndex((m) => m.id === next.id);
  if (index >= 0) {
    const prev = messages.value[index];
    // While a turn streams, the SSE 'chunk' handler grows the assistant content
    // in place. A concurrent history poll can carry an older, shorter snapshot;
    // merging it would truncate the live text and the next chunk would append
    // onto that stale prefix (garbled output). So for an in-flight assistant,
    // never let a merge shrink the content — SSE owns it until the turn ends,
    // at which point the terminal snapshot is at least as long and applies.
    const inFlightAssistant =
      prev.role === 'assistant' && (prev.status === 'pending' || prev.status === 'running');
    if (inFlightAssistant && next.content.length < prev.content.length) {
      next.content = prev.content;
    }
    messages.value[index] = {...prev, ...next};
  } else {
    messages.value.push(next);
    messages.value.sort((a, b) => a.id - b.id);
  }
  if (next.content) renderNow(messages.value.find((m) => m.id === next.id));
  scrollToBottom();
  syncPolling();
}

function assistantForTurn(turnId: number): ChatMessage | undefined {
  return messages.value.find((m) => m.turnId === turnId && m.role === 'assistant');
}

function setBubble(id: number, el: Element | null) {
  if (el) bubbleEls.set(id, el as HTMLElement);
  else bubbleEls.delete(id);
}

// Render a message's markdown through the Hub's own markup pipeline (server
// render + the global `.markup` enhancers: KaTeX, code highlight, mermaid) —
// the same path comment previews use, so chat content looks like comments.
async function renderMarkdown(message: ChatMessage | undefined) {
  if (!message || !message.content) return;
  // Snapshot the content we are about to render; if it changes while we await
  // /markup, a newer render will run and this (stale) response is discarded so
  // it can't overwrite the bubble with older partial content.
  const content = message.content;
  renderedContent.set(message.id, content);
  await nextTick();
  const el = bubbleEls.get(message.id);
  if (!el) return;
  try {
    const formData = new FormData();
    formData.append('mode', 'comment');
    formData.append('context', props.repoLink);
    formData.append('text', content);
    const res = await POST(`${props.repoLink}/markup`, {data: formData});
    if (message.content !== content) return; // superseded while awaiting
    if (!res.ok) {
      el.textContent = content;
      return;
    }
    const html = await res.text();
    if (message.content !== content) return; // superseded while awaiting
    renderPreviewPanelContent(el, html);
  } catch (err) {
    console.error('Failed to render DRCY markdown', err);
    if (message.content === content) el.textContent = content;
  }
  scrollToBottom();
}

function scheduleRender(message: ChatMessage | undefined) {
  // Skip when the current content is already what we last rendered/dispatched,
  // so repeated upserts of unchanged content (every poll tick) don't re-render.
  if (!message || renderedContent.get(message.id) === message.content) return;
  const existing = renderTimers.get(message.id);
  if (existing) clearTimeout(existing);
  renderTimers.set(
    message.id,
    window.setTimeout(() => {
      renderTimers.delete(message.id);
      renderMarkdown(message);
    }, RENDER_DEBOUNCE_MS),
  );
}

function renderNow(message: ChatMessage | undefined) {
  if (!message || renderedContent.get(message.id) === message.content) return;
  const existing = renderTimers.get(message.id);
  if (existing) {
    clearTimeout(existing);
    renderTimers.delete(message.id);
  }
  renderMarkdown(message);
}

// How close to the bottom (px) still counts as "following" the conversation.
const SCROLL_FOLLOW_THRESHOLD_PX = 80;

function isNearBottom(el: HTMLElement): boolean {
  return el.scrollHeight - el.scrollTop - el.clientHeight <= SCROLL_FOLLOW_THRESHOLD_PX;
}

function scrollToBottom() {
  // Respect a user who scrolled up to read earlier messages: only auto-follow
  // new content when they're already near the bottom. Checked synchronously
  // (before nextTick) so it reflects the position before this update rendered.
  const el = scroller.value;
  if (el && !isNearBottom(el)) return;
  nextTick(() => {
    const target = scroller.value;
    if (target) target.scrollTop = target.scrollHeight;
  });
}

function clearTranscript() {
  // The inline cards go with the transcript; a panel open on one would float.
  resolver.value = null;
  for (const timer of renderTimers.values()) clearTimeout(timer);
  renderTimers.clear();
  renderedContent.clear();
  bubbleEls.clear();
  messages.value = [];
  syncPolling();
}

function clearAll() {
  clearTranscript();
  artifacts.value = [];
  designChanges.value = [];
  files.value = [];
  activeArtifactId.value = null;
  activeChangeId.value = null;
  artifactSidebarOpen.value = false;
  editingArtifactId.value = null;
  resolver.value = null;
  artifactError.value = '';
  artifactNotice.value = '';
  artifactNoticeUrl.value = '';
  syncArtifactDirtyState();
}

function syncArtifactDirtyState() {
  document.dispatchEvent(new CustomEvent('drcy-chat-artifacts-dirty', {
    detail: artifacts.value.length > 0 || designChanges.value.length > 0 || files.value.length > 0,
  }));
}

async function loadFiles() {
  try {
    const res = await GET(`${baseUrl}/files?${scopeQuery}`);
    if (!res.ok) return;
    files.value = await res.json() ?? [];
    syncArtifactDirtyState();
  } catch (err) {
    console.error('Failed to load DRCY session files', err);
  }
}

function fileDownloadUrl(file: ChatFile): string {
  return `${baseUrl}/files/${file.id}/download?${scopeQuery}`;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(bytes < 10 * 1024 ? 1 : 0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function canPreviewFile(file: ChatFile): boolean {
  return file.extension === 'csv';
}

// Uploads may reach 16 MiB; the parser and the per-column width scan would
// hang the tab on an export that size. Matches the cap on a published sheet,
// so anything the table shows as an artifact previews as an upload too.
const FILE_PREVIEW_MAX_BYTES = 2 * 1024 * 1024;

async function toggleFilePreview(file: ChatFile) {
  if (previewFileId.value === file.id) {
    previewFileId.value = null;
    previewFileContent.value = '';
    return;
  }
  previewFileId.value = file.id;
  previewFileContent.value = '';
  previewFileError.value = '';
  if (file.size > FILE_PREVIEW_MAX_BYTES) {
    previewFileError.value = `Too large to preview (${formatFileSize(file.size)}). Download it instead.`;
    return;
  }
  previewFileLoading.value = true;
  try {
    const res = await GET(fileDownloadUrl(file));
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const text = await res.text();
    // The user may have moved on to another file while this one loaded.
    if (previewFileId.value === file.id) previewFileContent.value = text;
  } catch (error) {
    console.error(error);
    if (previewFileId.value === file.id) previewFileError.value = 'Could not load this file.';
  } finally {
    previewFileLoading.value = false;
  }
}

async function deleteFile(file: ChatFile) {
  if (!window.confirm(`Remove "${file.filename}" from this chat?`)) return;
  artifactError.value = '';
  if (previewFileId.value === file.id) previewFileId.value = null;
  const res = await DELETE(`${baseUrl}/files/${file.id}?${scopeQuery}&revision=${file.revision}`);
  if (!res.ok) {
    artifactError.value = res.status === 409 ?
      'This file changed elsewhere. Reloaded the latest files.' :
      'Could not remove this file.';
  }
  await loadFiles();
}

async function loadDesignChanges(reveal = false) {
  try {
    const res = await GET(`${baseUrl}/changes?${scopeQuery}`);
    if (!res.ok) return;
    const previousRevisions = new Map(designChanges.value.map((change) => [change.id, change.revision]));
    const nextChanges: DesignChange[] = await res.json() ?? [];
    const failed = new Set(failedChangePreviews.value);
    for (const change of nextChanges) {
      const previousRevision = previousRevisions.get(change.id);
      if (previousRevision !== undefined && previousRevision !== change.revision) {
        failed.delete(change.id);
      }
    }
    failedChangePreviews.value = failed;
    designChanges.value = nextChanges;
    const validIds = new Set(designChanges.value.map((change) => change.id));
    if (activeChangeId.value !== null && !validIds.has(activeChangeId.value)) {
      activeChangeId.value = designChanges.value[0]?.id ?? null;
    }
    if (designChanges.value.length && reveal) {
      artifactSidebarOpen.value = true;
      activeChangeId.value ??= designChanges.value[0]?.id ?? null;
    }
    syncArtifactDirtyState();
  } catch (err) {
    console.error('Failed to load DRCY design changes', err);
  }
}

async function loadArtifacts(reveal = false) {
  try {
    const res = await GET(`${baseUrl}/artifacts?${scopeQuery}`);
    if (!res.ok) return;
    const data = await res.json();
    artifacts.value = data ?? [];
    const validIds = new Set(artifacts.value.map((artifact) => artifact.id));
    if (activeArtifactId.value !== null && !validIds.has(activeArtifactId.value)) {
      activeArtifactId.value = artifacts.value[0]?.id ?? null;
    }
    if (artifacts.value.length === 0) {
      editingArtifactId.value = null;
      // Keep the sidebar open while a success notice is pending, so it survives
      // the committed draft leaving the list.
      if (!artifactNotice.value && designChanges.value.length === 0) artifactSidebarOpen.value = false;
    } else if (reveal) {
      artifactSidebarOpen.value = true;
      activeArtifactId.value ??= artifacts.value[0]?.id ?? null;
    }
    refreshCardDraftRevisions();
    syncArtifactDirtyState();
  } catch (err) {
    console.error('Failed to load DRCY artifacts', err);
  }
}

function toggleArtifactDetails(artifact: ChatArtifact) {
  if (activeArtifactId.value === artifact.id) {
    activeArtifactId.value = null;
    editingArtifactId.value = null;
    artifactError.value = '';
    return;
  }
  activeArtifactId.value = artifact.id;
  artifactSidebarOpen.value = true;
  editingArtifactId.value = null;
  artifactError.value = '';
}

function toggleChangeDetails(change: DesignChange) {
  activeChangeId.value = activeChangeId.value === change.id ? null : change.id;
  artifactSidebarOpen.value = true;
  artifactError.value = '';
}

function closeArtifactSidebar() {
  artifactSidebarOpen.value = false;
  editingArtifactId.value = null;
  artifactError.value = '';
  artifactNotice.value = '';
  artifactNoticeUrl.value = '';
}

function handleArtifactKeydown(event: KeyboardEvent) {
  if (event.key !== 'Escape') return;
  // Innermost first: the cell resolver, then the workspace, then the sidebar.
  // This listener is on window before the open workspace's own, so stopping
  // here keeps the workspace open when Escape only closes the resolver.
  if (resolver.value) {
    resolver.value = null;
    event.stopImmediatePropagation();
    return;
  }
  // The open workspace takes Escape itself.
  if (expandedArtifactId.value !== null) return;
  if (artifactSidebarOpen.value) closeArtifactSidebar();
}

// CSV sheets DRCY published in a turn show inline under its answer as editable
// tables, so the engineer works the sheet where the conversation is; the
// workspace keeps the download and commit actions.
function csvArtifactsForTurn(turnId: number): ChatArtifact[] {
  return artifacts.value.filter((artifact) => artifact.format === 'csv' && artifact.turn_id === turnId);
}

type CardDraft = {content: string; revision: number};
// Unsaved inline edits per artifact id. The revision is the sheet version Save
// must match; it follows the sheet when DRCY republishes it mid-edit so the
// engineer's edits stay saveable rather than being doomed to a 409.
const cardDrafts = ref<Record<number, CardDraft>>({});
const cardErrors = ref<Record<number, string>>({});

const SHEET_CHANGED_NOTICE = 'This sheet was republished while you were editing. Save replaces the new version with your edited copy of the previous one; Discard keeps the new version.';

function refreshCardDraftRevisions() {
  for (const artifact of artifacts.value) {
    const draft = cardDrafts.value[artifact.id];
    if (!draft || draft.revision === artifact.revision) continue;
    cardDrafts.value = {...cardDrafts.value, [artifact.id]: {...draft, revision: artifact.revision}};
    cardErrors.value = {...cardErrors.value, [artifact.id]: SHEET_CHANGED_NOTICE};
  }
}

// The cell resolver edits the same draft as the card and the workspace, so a
// value saved there lands in `cardDrafts` and goes out through the normal Save.
function openResolver(artifact: ChatArtifact, target: ResolveTarget) {
  const open = resolver.value;
  // The badge's own click never reaches the document, so the same cell
  // toggles the panel closed here.
  if (open && open.artifactId === artifact.id && open.revision === artifact.revision && open.target.rowIndex === target.rowIndex &&
    open.target.columnIndex === target.columnIndex && open.target.reference === target.reference) {
    resolver.value = null;
    return;
  }
  resolver.value = {artifactId: artifact.id, revision: artifact.revision, target};
}

function closeResolver() {
  resolver.value = null;
}

const CELL_MOVED_NOTICE = 'The sheet changed under the cell you were resolving, so nothing was written; open the cell again.';

function saveResolvedCell(value: string) {
  const open = resolver.value;
  resolver.value = null;
  if (!open) return;
  const artifact = artifacts.value.find((item) => item.id === open.artifactId);
  if (!artifact) return;
  const {rowIndex, columnIndex, referenceColumn, reference, header} = open.target;
  const table = parseCsv(cardContent(artifact));
  const row = table.rows[rowIndex];
  // Positions are only good for the sheet the badge was on; write nowhere
  // but the cell the engineer opened.
  if (!row || table.header[columnIndex] !== header || (row[referenceColumn] ?? '') !== reference) {
    cardErrors.value = {...cardErrors.value, [artifact.id]: CELL_MOVED_NOTICE};
    return;
  }
  if ((row[columnIndex] ?? '') === value) return;
  editCard(artifact, serializeCsv(setCsvCell(table, rowIndex, columnIndex, value)));
}

function askDrcyAboutCell(text: string) {
  resolver.value = null;
  // The workspace covers the composer; minimize first so the textarea is the
  // thing in focus. The question joins whatever the engineer already typed.
  if (expandedArtifactId.value !== null) minimizeWorkspace();
  document.dispatchEvent(new CustomEvent('drcy-chat-append', {detail: text}));
}

// A sheet that leaves the list, or comes back at another revision, takes its
// resolver with it: the cell it pointed at may have moved or settled, and the
// engineer reopens it from the sheet as it is now.
watch(artifacts, (list) => {
  const open = resolver.value;
  if (open && !list.some((artifact) => artifact.id === open.artifactId && artifact.revision === open.revision)) resolver.value = null;
});

function cardContent(artifact: ChatArtifact): string {
  return cardDrafts.value[artifact.id]?.content ?? artifact.content;
}

function editCard(artifact: ChatArtifact, content: string) {
  const revision = cardDrafts.value[artifact.id]?.revision ?? artifact.revision;
  cardDrafts.value = {...cardDrafts.value, [artifact.id]: {content, revision}};
  cardErrors.value = {...cardErrors.value, [artifact.id]: ''};
}

function discardCardDraft(artifact: ChatArtifact) {
  const {[artifact.id]: _dropped, ...rest} = cardDrafts.value;
  cardDrafts.value = rest;
  cardErrors.value = {...cardErrors.value, [artifact.id]: ''};
}

async function saveCardDraft(artifact: ChatArtifact) {
  const draft = cardDrafts.value[artifact.id];
  if (!draft) return;
  // Content only: a body without cell_notes keeps the sheet's provenance on
  // the Hub, so an engineer's edit never drops what the worker published.
  const res = await PATCH(`${baseUrl}/artifacts/${artifact.id}?${scopeQuery}`, {
    data: {revision: draft.revision, title: artifact.title, filename: artifact.filename, content: draft.content},
  });
  if (!res.ok) {
    // The draft survives a conflict: the reload adopts the sheet's new
    // revision so a second Save applies the edits, and Discard is the
    // engineer's call, not the error handler's.
    await loadArtifacts();
    cardErrors.value = {
      ...cardErrors.value,
      [artifact.id]: res.status === 409 ?
        'This sheet changed elsewhere. Your unsaved edits are kept; Save again to replace the latest version with them, or Discard them.' :
        'Could not save this sheet.',
    };
    return;
  }
  discardCardDraft(artifact);
  await loadArtifacts();
}

// The sheet workspace edits the same draft as the inline card, so nothing is
// lost switching between them.
const expandReturnFocus = ref<HTMLElement | null>(null);

function openArtifactInWorkspace(artifact: ChatArtifact, event?: Event) {
  expandReturnFocus.value = event?.currentTarget instanceof HTMLElement ? event.currentTarget : null;
  expandedArtifactId.value = artifact.id;
}

function minimizeWorkspace() {
  expandedArtifactId.value = null;
  resolver.value = null;
}

function artifactDownloadUrl(artifact: ChatArtifact): string {
  return `${baseUrl}/artifacts/${artifact.id}/download?${scopeQuery}`;
}

function artifactCellNotes(artifact: ChatArtifact) {
  return artifact.format === 'csv' ? parseCellNotes(artifact.cell_notes) : null;
}

// The sheet as a spreadsheet reads it: the per-cell provenance folded back
// into a Notes column, since a download has no hover bubbles.
function downloadSheetWithNotes(artifact: ChatArtifact) {
  const notes = artifactCellNotes(artifact);
  if (!notes) return;
  const sheet = serializeCsv(joinNotesColumn(parseCsv(artifact.content), notes));
  downloadTextFile(`${artifact.filename.replace(/\.csv$/i, '')}-with-notes.csv`, sheet, 'text/csv');
}

function changeDownloadUrl(change: DesignChange): string {
  return `${baseUrl}/changes/${change.id}/download?${scopeQuery}`;
}

function changePreviewUrl(change: DesignChange): string {
  return `${baseUrl}/changes/${change.id}/preview?${scopeQuery}&revision=${change.revision}`;
}

function markChangePreviewFailed(change: DesignChange) {
  failedChangePreviews.value = new Set([...failedChangePreviews.value, change.id]);
}

function markChangePreviewLoaded(change: DesignChange) {
  const failed = new Set(failedChangePreviews.value);
  failed.delete(change.id);
  failedChangePreviews.value = failed;
}

async function deleteDesignChange(change: DesignChange) {
  if (!window.confirm(`Discard "${change.title}"? This cannot be undone.`)) return;
  artifactError.value = '';
  const res = await DELETE(`${baseUrl}/changes/${change.id}?${scopeQuery}`, {
    data: {revision: change.revision},
  });
  if (!res.ok) {
    artifactError.value = res.status === 409 ?
      'This proposal changed elsewhere. Reloaded the latest proposal.' :
      'Could not discard this proposal.';
  }
  if (activeChangeId.value === change.id) activeChangeId.value = null;
  await loadDesignChanges();
}

function designChangeBranch(change: DesignChange): string {
  const stem = change.path.split('/').pop()?.replace(/\.[^.]+$/, '').toLowerCase()
    .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'schematic';
  return `drcy/changes/${stem}-${change.id}`;
}

async function applyDesignChange(change: DesignChange) {
  if (applyingChange.value) return;
  const branch = commitTargetBranch();
  if (!window.confirm(`Apply "${change.title}" to ${change.path} on the "${branch}" branch?`)) return;
  applyingChange.value = true;
  artifactError.value = '';
  artifactNotice.value = '';
  artifactNoticeUrl.value = '';
  try {
    const apply = (newBranch = '') => POST(`${baseUrl}/changes/${change.id}/apply?${scopeQuery}`, {
      data: {revision: change.revision, branch, new_branch: newBranch},
    });
    let res = await apply();
    if (res.status === 403) res = await apply(designChangeBranch(change));
    if (!res.ok) {
      artifactError.value = res.status === 409 ?
        'The source design changed. Ask DRCY to create a fresh proposal.' :
        'Could not apply this design change.';
      return;
    }
    const data = await res.json();
    artifactNotice.value = `Applied to the "${data.branch}" branch.`;
    artifactNoticeUrl.value = data.commit_url ?? '';
    await loadDesignChanges();
  } finally {
    applyingChange.value = false;
  }
}

function beginEdit(artifact: ChatArtifact) {
  editingArtifactId.value = artifact.id;
  editTitle.value = artifact.title;
  editFilename.value = artifact.filename;
  editContent.value = artifact.content;
  editRevision.value = artifact.revision;
  artifactError.value = '';
  artifactNotice.value = '';
  artifactNoticeUrl.value = '';
}

async function saveArtifact(artifact: ChatArtifact) {
  artifactError.value = '';
  const res = await PATCH(`${baseUrl}/artifacts/${artifact.id}?${scopeQuery}`, {
    data: {
      revision: editRevision.value,
      title: editTitle.value,
      filename: editFilename.value,
      content: editContent.value,
    },
  });
  if (!res.ok) {
    if (res.status === 409) {
      // The draft moved on. Exit edit mode so the stale editRevision can't drive
      // an endless 409 loop; reopening the reloaded draft re-captures its current
      // revision and content.
      artifactError.value = 'This artifact changed elsewhere. Reloaded the latest draft; reopen it to apply your edit.';
      editingArtifactId.value = null;
    } else {
      artifactError.value = 'Could not save this artifact.';
    }
    await loadArtifacts();
    return;
  }
  editingArtifactId.value = null;
  await loadArtifacts(true);
}

async function deleteArtifact(artifact: ChatArtifact) {
  if (!window.confirm(`Delete "${artifact.title}"? This cannot be undone.`)) return;
  artifactError.value = '';
  const res = await DELETE(`${baseUrl}/artifacts/${artifact.id}?${scopeQuery}`, {
    data: {revision: artifact.revision},
  });
  if (!res.ok) {
    artifactError.value = res.status === 409 ?
      'This artifact changed elsewhere. Reloaded the latest draft.' :
      'Could not delete this artifact.';
  }
  if (activeArtifactId.value === artifact.id) activeArtifactId.value = null;
  await loadArtifacts();
}

function artifactCommitMessage(artifact: ChatArtifact): string {
  return `docs: add DRCY artifact ${artifact.filename}`;
}

function artifactBranch(artifact: ChatArtifact): string {
  const stem = artifact.filename
    .replace(/\.[^.]+$/, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  return `drcy/artifacts/${stem || 'artifact'}-${artifact.id}`;
}

function commitTargetBranch(): string {
  if (props.commitBranch) return props.commitBranch;
  return props.scope.startsWith('repo:') ? props.scope.slice('repo:'.length) : props.defaultBranch;
}

async function commitArtifact(artifact: ChatArtifact) {
  if (committing.value) return;
  const branch = commitTargetBranch();
  if (!window.confirm(`Save "${artifact.filename}" to the "${branch}" branch of this repository?`)) return;
  committing.value = true;
  artifactError.value = '';
  artifactNotice.value = '';
  artifactNoticeUrl.value = '';
  try {
    const commit = (newBranch = '') => POST(`${baseUrl}/artifacts/commit?${scopeQuery}`, {
      data: {artifact_ids: [artifact.id], branch, new_branch: newBranch, message: artifactCommitMessage(artifact)},
    });
    // A protected target branch returns 403; retry once onto an auto-named
    // branch so the save still lands somewhere the user can open a PR from.
    let res = await commit();
    if (res.status === 403) res = await commit(artifactBranch(artifact));
    if (!res.ok) {
      artifactError.value = res.status === 403 ?
        'You need code write access to commit this artifact.' :
        'Could not commit this artifact.';
      return;
    }
    const data = await res.json();
    // Report the branch it actually landed on (which may be the fallback, not the
    // one named in the confirm) with a link, rather than navigating away. Set the
    // notice BEFORE reloading: committing the last draft empties the list, and
    // loadArtifacts must see a pending notice so it keeps the sidebar open to
    // render it.
    artifactNotice.value = `Saved to the "${data.branch}" branch.`;
    artifactNoticeUrl.value = data.commit_url ?? '';
    await loadArtifacts();
  } finally {
    committing.value = false;
  }
}

function warnBeforeUnload(event: BeforeUnloadEvent) {
  if (artifacts.value.length === 0 && designChanges.value.length === 0) return;
  event.preventDefault();
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  event.returnValue = '';
}

// Export the conversation as a Markdown file. Client-side only — the messages
// are already in memory — so no design data leaves the browser beyond what the
// user chooses to save.
function exportMarkdown() {
  const lines: string[] = [
    `# DRCY Chat: ${props.repoLink} (${props.scope})`,
    '',
  ];
  for (const message of messages.value) {
    const body = message.content.trim();
    if (!body) continue;
    lines.push(`**${authorName(message)}**`, '', body, '', '---', '');
  }
  downloadTextFile(`drcy-chat-${props.scope.replace(/[^a-z0-9]+/gi, '-')}.md`, lines.join('\n'), 'text/markdown');
}

// Open the browser print dialog. A scoped print stylesheet isolates the chat so
// "Save as PDF" produces a clean transcript with the rendered answers.
function printChat() {
  window.print();
}

// Example questions shown on the empty landing state. Clicking one prefills the
// composer (which lives in the page template, outside this component) via an
// event the glue listens for, so the user can review and send.
const examplePrompts = [
  'What does this design do?',
  'List the power rails and their sources.',
  'What are the main ICs and their functions?',
];
function prefill(text: string) {
  document.dispatchEvent(new CustomEvent('drcy-chat-prefill', {detail: text}));
}

async function loadHistory() {
  const request = ++historyRequests;
  const query = new URLSearchParams({scope: props.scope, ...contextParams});
  if (props.resume) {
    query.set('resume', '1');
    query.set('resume_context_key', props.resumeContextKey);
  }
  try {
    const res = await GET(`${baseUrl}/history?${query}`);
    if (!res.ok) return;
    const data = await res.json();
    // An earlier response landing last would drop the turn a later one already
    // loaded, and with no pending assistant left on screen polling stops too.
    if (request <= historyApplied) return;
    historyApplied = request;
    if (hasConversationContext || props.resume) {
      const returnedIDs = new Set<number>((data.messages ?? [])
        .map((entry: MessageEntry) => entry.id)
        .filter((id: number | undefined): id is number => id !== undefined));
      messages.value = messages.value.filter((message) => returnedIDs.has(message.id));
    }
    for (const entry of data.messages ?? []) upsert(entry);
  } catch (err) {
    console.error('Failed to load DRCY chat history', err);
  }
}

async function stopTurn(message: ChatMessage) {
  if (stoppingTurn.value !== null) return;
  stoppingTurn.value = message.turnId;
  try {
    if (!await confirmModal({header: props.stopConfirmTitle, content: props.stopConfirmMessage, confirmButtonColor: 'red'})) return;
    // Completion or End Chat can arrive while the engineer considers the dialog.
    if (!messages.value.some((current) => current.turnId === message.turnId && current.role === 'assistant' && (current.status === 'pending' || current.status === 'running'))) return;
    stopError.value = '';
    stopErrorTurn.value = null;
    const response = await POST(`${props.repoLink}/drcy_chat/stop?scope=${encodeURIComponent(props.scope)}`, {data: {turn_id: message.turnId}});
    if (!response.ok) throw new Error(`Stop response failed (${response.status})`);
    // Reconcile even if SSE is disconnected, and when completion won the race.
    await reconcile();
  } catch (err) {
    console.error('Failed to stop DRCY response', err);
    stopError.value = props.stopErrorLabel;
    stopErrorTurn.value = message.turnId;
  } finally {
    stoppingTurn.value = null;
  }
}

function hasInFlightTurn(): boolean {
  return messages.value.some(
    (m) => m.role === 'assistant' && (m.status === 'pending' || m.status === 'running'),
  );
}

// Start polling when a turn is in flight, stop when none is; also tell the
// composer (which lives in the page template, outside this component) to disable
// send while a turn responds and re-enable it once done. Idempotent, so it's
// safe to call after any state change (SSE event, upsert, or a poll tick).
function syncPolling() {
  const busy = hasInFlightTurn();
  if (busy) {
    if (pollTimer === null) {
      pollTimer = window.setInterval(reconcile, POLL_INTERVAL_MS);
    }
  } else if (pollTimer !== null) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
  if (busy !== lastBusy) {
    lastBusy = busy;
    document.dispatchEvent(new CustomEvent('drcy-chat-busy', {detail: busy}));
  }
}

// Everything the panel shows that a missed event would otherwise strand.
// Stream events are fire-and-forget: one published while EventSource was
// disconnected, or by another Hub process with no broker replay, is simply
// gone. Reading these back on reconnect and while polling is what keeps a
// proposal or artifact created in that gap from staying invisible until a full
// page reload. Neither reveals the sidebar: a reconciliation is not the same
// event as a creation.
function reconcile() {
  loadHistory();
  loadArtifacts();
  loadDesignChanges();
  loadFiles();
}

function activityKind(kind: unknown, label: string): ActivityKind {
  if (kind === 'tool' || kind === 'status') return kind;
  // Compatibility with a Hub deployed before explicit activity kinds.
  return label === 'thinking' || label === 'Compacting context...' ? 'status' : 'tool';
}

function openStream() {
  eventSource = new EventSource(`${baseUrl}/stream?${scopeQuery}`);
  eventSource.addEventListener('open', () => {
    // Reconcile on every open, including the first. Because the stream is
    // subscribed before this catch-up read, no message posted during the read is
    // missed (it is delivered live), and reconnects (server restart, network
    // blip) also catch up instead of hanging on a stale turn.
    reconcile();
  });
  eventSource.addEventListener('message', () => {
    // The broker is scope-wide while histories are context-specific. Reconcile
    // through the filtered endpoint so neither a contextual nor the plain tab
    // briefly renders another conversation's message.
    loadHistory();
  });
  eventSource.addEventListener('chunk', (event: MessageEvent) => {
    const {turn_id: turnId, content} = JSON.parse(event.data);
    const assistant = assistantForTurn(turnId);
    if (assistant && (assistant.status === 'pending' || assistant.status === 'running')) {
      assistant.content += content;
      if (assistant.status === 'pending') assistant.status = 'running';
      scheduleRender(assistant);
      scrollToBottom();
    }
  });
  eventSource.addEventListener('activity', (event: MessageEvent) => {
    const {turn_id: turnId, label, kind} = JSON.parse(event.data);
    const assistant = assistantForTurn(turnId);
    if (assistant && label && (assistant.status === 'pending' || assistant.status === 'running')) {
      // Status updates share the activity channel with tool calls.
      assistant.activity = label;
      assistant.activityKind = activityKind(kind, label);
      if (assistant.activityKind === 'tool') assistant.toolCount += 1;
      scrollToBottom();
    }
  });
  eventSource.addEventListener('queue', (event: MessageEvent) => {
    const {turn_id: turnId, phase} = JSON.parse(event.data);
    if (phase === 'starting') startingTurns.value.add(turnId);
  });
  eventSource.addEventListener('status', (event: MessageEvent) => {
    const {turn_id: turnId, status, content} = JSON.parse(event.data);
    const assistant = assistantForTurn(turnId);
    if (assistant && assistant.status !== 'cancelled') {
      assistant.status = status;
      if (content) assistant.content = content;
      if (status === 'completed' || status === 'failed' || status === 'cancelled') {
        assistant.activity = ''; // stop the live tool line; the count remains
        renderNow(assistant);
      } else {
        scheduleRender(assistant);
      }
      scrollToBottom();
      syncPolling();
    }
  });
  eventSource.addEventListener('transcript-reset', () => clearTranscript());
  eventSource.addEventListener('cleared', () => clearAll());
  eventSource.addEventListener('artifact', (event: MessageEvent) => {
    // Reveal the sidebar only when a new artifact is created; an update, commit,
    // or delete refreshes the list without forcing the panel open each time.
    let action = '';
    try {
      action = JSON.parse(event.data).action;
    } catch {
      // Non-JSON payload: refresh without revealing.
    }
    loadArtifacts(action === 'created');
  });
  // Uploads and removals refresh the file list; the chip row shows them, so
  // the sidebar is never forced open for a file.
  eventSource.addEventListener('file', () => loadFiles());
  eventSource.addEventListener('design-change', (event: MessageEvent) => {
    let action = '';
    try {
      action = JSON.parse(event.data).action;
    } catch {
      // Non-JSON payload: refresh without revealing.
    }
    loadDesignChanges(action === 'created');
  });
  eventSource.addEventListener('error', () => {
    // EventSource auto-reconnects on a transient error (and its 'open' handler
    // re-runs the history catch-up). Until it recovers — or if it can't — fall
    // back to the history poll so an in-flight turn still reconciles and reaches
    // a terminal state instead of spinning. syncPolling is a no-op when nothing
    // is in flight, so this costs nothing on an idle stream.
    syncPolling();
  });
}

// The composer glue (features/drcy-chat.ts) lives outside this component and
// coordinates through the server, but on a successful send/end it also signals us
// directly so the view updates even when SSE is dead or on another Hub process:
// 'reconcile' reloads history (showing the new turn and starting the fallback
// poll), 'ended' clears the transcript locally.
function onReconcile(event: Event) {
  const detail = (event as CustomEvent).detail as
    {turnId?: number; dispatched?: boolean; queuedBehind?: boolean} | undefined;
  if (typeof detail?.turnId === 'number' && typeof detail.dispatched === 'boolean') {
    sentTurns.value.set(detail.turnId, {
      dispatched: detail.dispatched,
      queuedBehind: detail.queuedBehind === true,
    });
  }
  loadHistory();
}
function onEnded() {
  clearAll();
}
// The composer glue uploads files itself and signals us to refresh, so a new
// chip appears even when the SSE stream is dead or on another Hub process.
function onFilesChanged() {
  loadFiles();
}

function onTranscriptReset() {
  clearTranscript();
}

onMounted(() => {
  // Open the stream first; its 'open' handler does the initial history load, so
  // the subscription is active before the catch-up read (no missed-message gap).
  openStream();
  loadArtifacts(true);
  loadDesignChanges(true);
  loadFiles();
  responseControlsTarget.value = document.querySelector('#drcy-chat-response-controls') ? '#drcy-chat-response-controls' : null;
  fileChipTarget.value = document.querySelector('#drcy-chat-file-chips') ? '#drcy-chat-file-chips' : null;
  window.addEventListener('beforeunload', warnBeforeUnload);
  window.addEventListener('keydown', handleArtifactKeydown);
  document.addEventListener('drcy-chat-reconcile', onReconcile);
  document.addEventListener('drcy-chat-transcript-reset', onTranscriptReset);
  document.addEventListener('drcy-chat-ended', onEnded);
  document.addEventListener('drcy-chat-files-changed', onFilesChanged);
});

onUnmounted(() => {
  if (eventSource) eventSource.close();
  if (pollTimer !== null) clearInterval(pollTimer);
  if (queuedNoticeTimer !== null) clearTimeout(queuedNoticeTimer);
  for (const timer of renderTimers.values()) clearTimeout(timer);
  renderTimers.clear();
  window.removeEventListener('beforeunload', warnBeforeUnload);
  window.removeEventListener('keydown', handleArtifactKeydown);
  document.removeEventListener('drcy-chat-reconcile', onReconcile);
  document.removeEventListener('drcy-chat-transcript-reset', onTranscriptReset);
  document.removeEventListener('drcy-chat-ended', onEnded);
  document.removeEventListener('drcy-chat-files-changed', onFilesChanged);
});
</script>

<template>
  <div class="drcy-chat">
    <div v-if="messages.length || artifacts.length || designChanges.length || files.length" class="drcy-chat-toolbar tw:flex tw:justify-end tw:gap-2 tw:mb-2">
      <button
        v-if="designChanges.length"
        class="ui tiny basic button"
        type="button"
        :aria-expanded="artifactSidebarOpen"
        aria-controls="drcy-chat-artifact-sidebar"
        @click="artifactSidebarOpen = true"
      >
        Proposed changes <span class="ui mini label">{{ designChanges.length }}</span>
      </button>
      <button
        v-if="artifacts.length"
        class="ui tiny basic button"
        type="button"
        :aria-expanded="artifactSidebarOpen"
        aria-controls="drcy-chat-artifact-sidebar"
        @click="artifactSidebarOpen = true"
      >
        Artifacts <span class="ui mini label">{{ artifacts.length }}</span>
      </button>
      <button
        v-if="files.length"
        class="ui tiny basic button"
        type="button"
        :aria-expanded="artifactSidebarOpen"
        aria-controls="drcy-chat-artifact-sidebar"
        @click="artifactSidebarOpen = true"
      >
        Files <span class="ui mini label">{{ files.length }}</span>
      </button>
      <button v-if="messages.length" class="ui tiny basic button" type="button" @click="exportMarkdown">
        Export Markdown
      </button>
      <button v-if="messages.length" class="ui tiny basic button" type="button" @click="printChat">
        Save as PDF
      </button>
    </div>
    <div v-if="messages.length || !contextual" ref="scroller" class="drcy-chat-messages">
    <div v-if="messages.length === 0" class="drcy-chat-empty">
      <img :src="drcyAvatar" class="drcy-chat-empty-avatar" alt="">
      <div class="drcy-chat-empty-title">Ask DRCY Chat about this design</div>
      <div class="drcy-chat-empty-subtitle">
        Ask about components, nets, connections, or part specs. Every answer is backed by evidence you can check.
      </div>
      <div class="drcy-chat-empty-examples">
        <button
          v-for="(example, i) in examplePrompts"
          :key="i"
          type="button"
          class="ui basic button drcy-chat-example"
          @click="prefill(example)"
        >{{ example }}</button>
      </div>
    </div>
    <div v-else class="comment-list">
     <div class="timeline">
      <div
        v-for="message in messages"
        :key="message.id"
        class="timeline-item comment"
        :class="{'drcy-chat-failed': message.status === 'failed'}"
      >
        <a class="timeline-avatar" :href="avatarHref(message)">
          <img class="ui avatar" :src="avatarSrc(message)" width="40" height="40" alt="">
        </a>
        <div class="content comment-container">
          <div class="comment-header" role="heading" aria-level="3">
            <div class="comment-header-left tw:flex tw:items-center">
              <span class="text grey muted-links">
                <strong class="tw:text-text-dark">{{ authorName(message) }}</strong>
              </span>
            </div>
          </div>
          <div class="ui attached segment comment-body" role="article">
            <!-- Streamed/final answer. Kept in the DOM (v-show) so its render
                 target is stable while chunks arrive. -->
            <div v-show="message.content" :ref="(el) => setBubble(message.id, el as Element | null)"></div>
            <!-- Busy state: the animation until text starts, and a single live
                 line for DRCY's current status or tool. -->
            <template
              v-if="message.role === 'assistant' && (message.status === 'pending' || message.status === 'running')"
            >
              <img v-if="!message.content" :src="thinkingSrc" class="drcy-chat-thinking-anim" alt="">
            </template>
            <!-- The spoken copy of the busy state. Rendered for every assistant
                 message, empty when idle, so the region exists before it has
                 anything to say and a change is announced rather than silently
                 appearing beside the composer. -->
            <div
              v-if="message.role === 'assistant'"
              class="drcy-chat-status" :class="{'tw:mt-2': message.content, 'drcy-chat-tool': message.activity && !queuedNotice(message)}"
              role="status" aria-live="polite"
            >
              {{ statusLine(message) }}
            </div>
            <!-- Once the turn is done, summarize how many tools ran. -->
            <div
              v-if="message.role === 'assistant' && (message.status === 'completed' || message.status === 'failed' || message.status === 'cancelled') && message.toolCount"
              class="drcy-chat-toolcount tw:mt-2"
            >
              {{ message.toolCount }} tool call{{ message.toolCount === 1 ? '' : 's' }}
            </div>
            <!-- Was this answer any good? Only a finished answer can be
                 rated, and only in this reader's own chat. -->
            <div
              v-if="message.role === 'assistant' && message.status === 'completed'"
              class="drcy-chat-rating tw:mt-2 tw:flex tw:items-center tw:gap-2"
            >
              <button
                type="button"
                class="ui mini basic button drcy-chat-rate-up"
                :class="{'drcy-chat-rated': message.rating === 'up'}"
                :aria-pressed="message.rating === 'up'"
                title="This answer was helpful"
                @click="rate(message, 'up')"
              >
                <SvgIcon name="octicon-thumbsup" :size="14"/>
              </button>
              <button
                type="button"
                class="ui mini basic button drcy-chat-rate-down"
                :class="{'drcy-chat-rated': message.rating === 'down'}"
                :aria-pressed="message.rating === 'down'"
                title="This answer was not helpful"
                @click="rate(message, 'down')"
              >
                <SvgIcon name="octicon-thumbsdown" :size="14"/>
              </button>
              <!-- The note reads back beside the verdict: a reader who wrote
                   one should not have to open an editor to see it again. -->
              <span v-if="message.ratingNote && !message.noteOpen" class="tw:text-text-light-2 tw:text-xs">
                {{ message.ratingNote }}
              </span>
              <button
                v-if="message.ratingNote && !message.noteOpen"
                type="button"
                class="ui mini basic button drcy-chat-rate-note"
                @click="message.noteOpen = true; message.noteDraft = message.ratingNote"
              >
                Edit
              </button>
            </div>
            <div v-if="message.noteOpen" class="drcy-chat-rating-note tw:mt-2 tw:flex tw:items-center tw:gap-2">
              <span v-if="message.noteError" class="tw:text-red tw:text-sm" role="alert">{{ message.noteError }}</span>
              <div class="ui mini input drcy-chat-rating-note-field">
                <input
                  v-model="message.noteDraft"
                  type="text"
                  class="drcy-chat-rating-note-input"
                  :maxlength="1000"
                  :placeholder="
                    message.rating === 'down' ? 'What was wrong? (optional)' : 'What worked? (optional)'
                  "
                  @keyup.enter="saveRatingNote(message)"
                  @keyup.esc="message.noteOpen = false"
                >
              </div>
              <button type="button" class="ui mini primary button" @click="saveRatingNote(message)">
                Save
              </button>
              <button type="button" class="ui mini basic button" @click="message.noteOpen = false">
                Skip
              </button>
            </div>
            <section
              v-for="artifact in (message.role === 'assistant' ? csvArtifactsForTurn(message.turnId) : [])"
              :key="artifact.id"
              class="drcy-chat-artifact-card"
              :aria-label="`Sheet ${artifact.filename}`"
            >
              <header class="drcy-chat-artifact-card-header">
                <span class="drcy-artifact-list-copy">
                  <span class="drcy-artifact-list-title">{{ artifact.title }}</span>
                  <span class="tw:text-text-light-2 tw:text-xs">{{ artifact.filename }} · rev {{ artifact.revision }}</span>
                </span>
                <button class="ui tiny basic button" type="button" @click="openArtifactInWorkspace(artifact, $event)">Open in workspace</button>
              </header>
              <DrcyCsvTable
                :content="cardContent(artifact)"
                :cell-notes="artifact.cell_notes"
                editable
                interactive
                @update:content="editCard(artifact, $event)"
                @resolve-cell="openResolver(artifact, $event)"
              />
              <footer v-if="cardDrafts[artifact.id] || cardErrors[artifact.id]" class="drcy-chat-artifact-card-footer">
                <span v-if="cardErrors[artifact.id]" class="tw:text-red tw:text-sm" role="alert">{{ cardErrors[artifact.id] }}</span>
                <template v-if="cardDrafts[artifact.id]">
                  <span class="tw:text-text-light-2 tw:text-sm">Unsaved changes</span>
                  <button class="ui tiny primary button" type="button" @click="saveCardDraft(artifact)">Save</button>
                  <button class="ui tiny basic button" type="button" @click="discardCardDraft(artifact)">Discard</button>
                </template>
              </footer>
            </section>
          </div>
        </div>
      </div>
     </div>
    </div>
    </div>
    <DrcySheetWorkspace
      v-if="expandedArtifact"
      open
      :label="`Workspace: ${expandedArtifact.title}`"
      :return-focus="expandReturnFocus"
      @close="minimizeWorkspace"
    >
      <template #header>
        <span class="drcy-artifact-list-copy drcy-sheet-workspace-copy">
          <span class="drcy-sheet-workspace-title">{{ expandedArtifact.title }}</span>
          <span class="tw:text-text-light-2 tw:text-sm">{{ expandedArtifact.filename }} · rev {{ expandedArtifact.revision }}</span>
        </span>
        <span v-if="cardErrors[expandedArtifact.id]" class="tw:text-red tw:text-sm" role="alert">{{ cardErrors[expandedArtifact.id] }}</span>
        <template v-if="cardDrafts[expandedArtifact.id]">
          <span class="tw:text-text-light-2 tw:text-sm">Unsaved changes</span>
          <button class="ui small primary button" type="button" @click="saveCardDraft(expandedArtifact)">Save</button>
          <button class="ui small basic button" type="button" @click="discardCardDraft(expandedArtifact)">Discard</button>
        </template>
      </template>
      <DrcyCsvTable
        :key="expandedArtifact.id"
        :content="cardContent(expandedArtifact)"
        :cell-notes="expandedArtifact.cell_notes"
        editable
        interactive
        @update:content="editCard(expandedArtifact, $event)"
        @resolve-cell="openResolver(expandedArtifact, $event)"
      />
    </DrcySheetWorkspace>
    <DrcyCellResolver :target="resolver?.target ?? null" @save="saveResolvedCell" @ask="askDrcyAboutCell" @close="closeResolver"/>
    <Teleport v-if="responseControlsTarget" :to="responseControlsTarget">
      <template v-if="activeResponse">
        <span v-if="stopError && stopErrorTurn === activeResponse.turnId" class="tw:text-red tw:text-sm" role="alert">{{ stopError }}</span>
        <button class="ui basic red button drcy-chat-stop" type="button" :disabled="stoppingTurn !== null" @click="stopTurn(activeResponse)">{{ stopLabel }}</button>
      </template>
    </Teleport>
    <Teleport v-if="fileChipTarget" :to="fileChipTarget">
      <ul v-if="files.length" class="drcy-file-chips" aria-label="Files attached to this chat">
        <li v-for="file in files" :key="file.id" class="drcy-file-chip">
          <a :href="fileDownloadUrl(file)" :title="`${file.filename} (${formatFileSize(file.size)})`">{{ file.filename }}</a>
          <button
            type="button"
            class="drcy-file-chip-remove"
            :aria-label="`Remove ${file.filename}`"
            @click="deleteFile(file)"
          >×</button>
        </li>
      </ul>
    </Teleport>
    <Teleport to="#drcy-chat-artifact-sidebar">
      <Transition name="drcy-artifact-drawer">
        <aside
          v-if="(artifacts.length || designChanges.length || files.length || artifactNotice) && artifactSidebarOpen"
          class="ui segment drcy-artifact-sidebar"
          aria-label="DRCY workspace"
        >
        <header class="drcy-artifact-sidebar-header">
          <div class="tw:min-w-0">
            <div class="tw:flex tw:items-center tw:gap-2">
              <h3 class="tw:m-0">DRCY Workspace</h3>
              <span class="ui mini label">{{ artifacts.length + designChanges.length + files.length }}</span>
            </div>
          </div>
          <button
            class="ui tiny basic button drcy-artifact-close"
            type="button"
            aria-label="Collapse artifact sidebar"
            @click="closeArtifactSidebar"
          >
            Hide
          </button>
        </header>

        <div v-if="artifactError" class="ui negative message tw:mx-3 tw:my-2 tw:py-2">{{ artifactError }}</div>
        <div v-else-if="artifactNotice" class="ui positive message tw:mx-3 tw:my-2 tw:py-2">
          {{ artifactNotice }}
          <a v-if="artifactNoticeUrl" :href="artifactNoticeUrl">View commit</a>
        </div>

        <div class="drcy-artifact-list" role="list" aria-label="DRCY drafts">
          <section v-if="designChanges.length" class="drcy-workspace-section">
            <h4 class="drcy-workspace-section-title">Proposed design changes</h4>
            <article
              v-for="change in designChanges"
              :key="`change-${change.id}`"
              class="drcy-artifact-list-item"
              :class="{'active': activeChangeId === change.id}"
              role="listitem"
            >
              <button
                type="button"
                class="drcy-artifact-list-button"
                :aria-expanded="activeChangeId === change.id"
                @click="toggleChangeDetails(change)"
              >
                <span class="drcy-artifact-list-copy">
                  <span class="drcy-artifact-list-title">{{ change.title }}</span>
                  <span class="tw:text-text-light-2 tw:text-xs">{{ change.path }}</span>
                </span>
                <span class="ui mini blue label">SCHDOC</span>
                <span class="drcy-artifact-toggle" aria-hidden="true">
                  {{ activeChangeId === change.id ? '−' : '+' }}
                </span>
              </button>
              <div v-if="activeChangeId === change.id" class="drcy-artifact-detail">
                <div class="drcy-change-preview-heading">
                  <strong>Proposed schematic</strong>
                  <a :href="changePreviewUrl(change)" target="_blank" rel="noopener">Open full size</a>
                </div>
                <div class="drcy-change-preview">
                  <div v-if="failedChangePreviews.has(change.id)" class="tw:text-text-light-2 tw:p-3">
                    The visual preview could not be rendered. The verified property changes are still available below.
                  </div>
                  <img
                    v-else
                    :src="changePreviewUrl(change)"
                    :alt="`Proposed schematic for ${change.title}`"
                    @load="markChangePreviewLoaded(change)"
                    @error="markChangePreviewFailed(change)"
                  >
                </div>
                <dl class="drcy-change-summary">
                  <template
                    v-for="(item, itemIndex) in change.validation.changes ?? []"
                    :key="`${item.target ?? ''}:${item.property}:${itemIndex}`"
                  >
                    <dt>{{ item.target ? `${item.target} ${item.property}` : item.property }}</dt>
                    <dd><code>{{ item.before ?? '(missing)' }}</code> → <code>{{ item.after }}</code></dd>
                  </template>
                </dl>
                <div v-if="change.validation.checks?.length" class="ui small positive message drcy-change-validation">
                  <strong>Verified</strong>
                  <ul>
                    <li v-for="check in change.validation.checks" :key="check">{{ check }}</li>
                  </ul>
                </div>
                <!-- A caution about the whole proposal, not a property that changed. Rendered
                     apart from the summary rows so it reads as one, rather than as a diff. -->
                <div v-if="change.validation.warnings?.length" class="ui small warning message drcy-change-validation">
                  <strong>Before you commit</strong>
                  <ul>
                    <li v-for="warning in change.validation.warnings" :key="warning">{{ warning }}</li>
                  </ul>
                </div>
                <div class="drcy-change-actions" aria-label="Proposed design change actions">
                  <a class="ui tiny basic button" :href="changeDownloadUrl(change)">Download</a>
                  <button class="ui tiny primary button" type="button" :disabled="applyingChange" @click="applyDesignChange(change)">
                    {{ applyingChange ? 'Applying…' : 'Apply to repo' }}
                  </button>
                  <button class="ui tiny basic red button" type="button" @click="deleteDesignChange(change)">Discard</button>
                </div>
              </div>
            </article>
          </section>

          <section v-if="artifacts.length" class="drcy-workspace-section">
            <h4 class="drcy-workspace-section-title">Artifacts</h4>
          <article
            v-for="artifact in artifacts"
            :key="artifact.id"
            class="drcy-artifact-list-item"
            :class="{'active': activeArtifactId === artifact.id}"
            role="listitem"
          >
            <button
              type="button"
              class="drcy-artifact-list-button"
              :aria-expanded="activeArtifactId === artifact.id"
              @click="toggleArtifactDetails(artifact)"
            >
              <span class="drcy-artifact-list-copy">
                <span class="drcy-artifact-list-title">{{ artifact.title }}</span>
                <span class="tw:text-text-light-2 tw:text-xs">{{ artifact.filename }}</span>
              </span>
              <span class="ui mini label">{{ artifact.format.toUpperCase() }}</span>
              <span class="drcy-artifact-toggle" aria-hidden="true">
                {{ activeArtifactId === artifact.id ? '−' : '+' }}
              </span>
            </button>
            <div v-if="activeArtifactId === artifact.id" class="drcy-artifact-detail">
              <template v-if="editingArtifactId === artifact.id">
                <div class="ui form drcy-artifact-edit-form">
                  <div class="field">
                    <label :for="`drcy-artifact-title-${artifact.id}`">Title</label>
                    <input :id="`drcy-artifact-title-${artifact.id}`" v-model="editTitle">
                  </div>
                  <div class="field">
                    <label :for="`drcy-artifact-filename-${artifact.id}`">Filename</label>
                    <input :id="`drcy-artifact-filename-${artifact.id}`" v-model="editFilename">
                  </div>
                  <div class="field drcy-artifact-content-field">
                    <label :for="`drcy-artifact-content-${artifact.id}`">Content</label>
                    <DrcyCsvTable
                      v-if="artifact.format === 'csv'"
                      :id="`drcy-artifact-content-${artifact.id}`"
                      :content="editContent"
                      :cell-notes="artifact.cell_notes"
                      editable
                      @update:content="editContent = $event"
                    />
                    <textarea v-else :id="`drcy-artifact-content-${artifact.id}`" v-model="editContent" class="drcy-artifact-editor"></textarea>
                  </div>
                  <div class="tw:flex tw:gap-2">
                    <button class="ui small primary button" type="button" @click="saveArtifact(artifact)">Save</button>
                    <button class="ui small basic button" type="button" @click="editingArtifactId = null">Cancel</button>
                  </div>
                </div>
              </template>
              <template v-else>
                <div class="drcy-artifact-actions" aria-label="Artifact actions">
                  <button class="ui tiny basic button" type="button" @click="beginEdit(artifact)">
                    Edit
                  </button>
                  <a class="ui tiny basic button" :href="artifactDownloadUrl(artifact)">
                    Download
                  </a>
                  <button v-if="artifactCellNotes(artifact)" class="ui tiny basic button" type="button" @click="downloadSheetWithNotes(artifact)">
                    Download with notes
                  </button>
                  <button class="ui tiny basic button" type="button" :disabled="committing" @click="commitArtifact(artifact)">
                    {{ committing ? 'Saving…' : 'Save to repo' }}
                  </button>
                  <button class="ui tiny basic red button" type="button" @click="deleteArtifact(artifact)">
                    Delete
                  </button>
                </div>
                <DrcyCsvTable v-if="artifact.format === 'csv'" class="drcy-artifact-csv" :content="artifact.content" :cell-notes="artifact.cell_notes"/>
                <pre v-else class="drcy-artifact-preview">{{ artifact.content }}</pre>
              </template>
            </div>
          </article>
          </section>

          <section v-if="files.length" class="drcy-workspace-section">
            <h4 class="drcy-workspace-section-title">Uploaded files</h4>
            <article
              v-for="file in files"
              :key="`file-${file.id}`"
              class="drcy-artifact-list-item drcy-file-list-item"
              role="listitem"
            >
              <div class="drcy-artifact-list-button drcy-file-row">
                <span class="drcy-artifact-list-copy">
                  <span class="drcy-artifact-list-title">{{ file.filename }}</span>
                  <span class="tw:text-text-light-2 tw:text-xs">{{ formatFileSize(file.size) }}</span>
                </span>
                <span class="ui mini label">{{ file.extension.toUpperCase() }}</span>
                <button
                  v-if="canPreviewFile(file)"
                  class="ui tiny basic button"
                  type="button"
                  :aria-expanded="previewFileId === file.id"
                  @click="toggleFilePreview(file)"
                >
                  {{ previewFileId === file.id ? 'Hide' : 'Preview' }}
                </button>
                <a class="ui tiny basic button" :href="fileDownloadUrl(file)">Download</a>
                <button class="ui tiny basic red button" type="button" @click="deleteFile(file)">Remove</button>
              </div>
              <div v-if="previewFileId === file.id" class="drcy-artifact-detail drcy-file-preview">
                <p v-if="previewFileLoading" class="tw:text-text-light-2 tw:m-0">Loading…</p>
                <p v-else-if="previewFileError" class="tw:text-red tw:m-0" role="alert">{{ previewFileError }}</p>
                <DrcyCsvTable v-else :content="previewFileContent"/>
              </div>
            </article>
          </section>
        </div>
        </aside>
      </Transition>
    </Teleport>
  </div>
</template>

<style scoped>
.drcy-chat-messages {
  min-height: 12rem;
  max-height: 60vh;
  overflow-y: auto;
}
.drcy-chat-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-height: 15rem;
  text-align: center;
  padding: 1.5rem 1rem;
}
.drcy-chat-empty-avatar {
  width: 56px;
  height: 56px;
  border-radius: 50%;
}
.drcy-chat-empty-title {
  font-size: 1.15em;
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
}
.drcy-chat-empty-subtitle {
  color: var(--color-text-light-2);
  max-width: 34rem;
}
.drcy-chat-empty-examples {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: center;
  margin-top: 0.5rem;
}
.drcy-chat-thinking-anim {
  display: block;
  height: 72px;
  width: auto;
}
.drcy-chat-tool {
  color: var(--color-text-light-2);
  font-size: 0.85em;
  font-family: var(--fonts-monospace);
}
.drcy-chat-status {
  color: var(--color-text-light-2);
  font-size: 0.85em;
}
.drcy-chat-status:empty {
  display: none;
}
.drcy-chat-toolcount {
  color: var(--color-text-light-3);
  font-size: 0.8em;
}
.drcy-chat-rating .ui.button {
  color: var(--color-text-light-2);
}
.drcy-chat-rated.ui.button {
  color: var(--color-primary) !important;
  border-color: var(--color-primary) !important;
}
/* Wide enough for a sentence, and it gives the row back on small screens. */
.drcy-chat-rating-note-field {
  flex: 1 1 24rem;
  max-width: 32rem;
}
.drcy-chat-rating-note {
  flex-wrap: wrap;
}
.drcy-chat-failed .comment-body {
  border-color: var(--color-red);
}
.drcy-artifact-sidebar {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: min(calc(100vh - 7.5rem), 64rem);
  min-height: 38rem;
  margin: 0 !important;
  padding: 0 !important;
  overflow: hidden;
  background: var(--color-body);
  border: 1px solid var(--color-secondary) !important;
  border-radius: var(--border-radius) !important;
}
.drcy-artifact-sidebar-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  border-bottom: 1px solid var(--color-secondary);
}
.drcy-artifact-close {
  margin: 0 !important;
  padding-right: 0.8rem !important;
  padding-left: 0.8rem !important;
}
.drcy-artifact-list {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 0.5rem;
  min-height: 0;
  overflow-y: auto;
  padding: 0.75rem;
}
.drcy-workspace-section {
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  gap: 0.5rem;
}
/* The section holding the expanded item takes the remaining workspace height
   so the item's detail (a virtualized table, a preview) scrolls inside it
   instead of stretching the whole list. It keeps its content minimum (no
   min-height: 0): shrinking below the item's floor made the item paint over
   the sections after it. When the workspace is short, the list scrolls. */
.drcy-workspace-section:has(> .drcy-artifact-list-item.active) {
  flex: 1 1 auto;
}
.drcy-workspace-section-title {
  margin: 0.25rem 0 0;
  color: var(--color-text-light-2);
  font-size: 0.8rem;
  font-weight: var(--font-weight-semibold);
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.drcy-artifact-list-item {
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  overflow: hidden;
  background: var(--color-body);
  border: 1px solid var(--color-secondary);
  border-radius: var(--border-radius);
}
.drcy-artifact-list-item.active {
  flex: 1 1 auto;
  min-height: 20rem;
  border-color: var(--color-secondary-dark-1);
}
.drcy-artifact-list-button {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 0.65rem;
  width: 100%;
  min-width: 0;
  padding: 0.65rem 0.75rem;
  color: inherit;
  text-align: left;
  background: var(--color-secondary-alpha-20);
  border: 0;
  cursor: pointer;
}
.drcy-artifact-list-copy {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-width: 0;
}
.drcy-artifact-list-copy > span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.drcy-artifact-list-title {
  font-weight: var(--font-weight-semibold);
}
.drcy-artifact-toggle {
  flex: 0 0 1rem;
  font-size: 1.15rem;
  font-weight: var(--font-weight-semibold);
  text-align: center;
}
.drcy-artifact-detail {
  container-type: inline-size;
  /* Caps the table viewport's intrinsic height; without it the section's
     content minimum would be the whole sheet. */
  --drcy-csv-max-height: 60vh;
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  overflow-y: auto;
  padding: 0.75rem;
  border-top: 1px solid var(--color-secondary);
}
.drcy-artifact-actions {
  /* Buttons keep their label's width and wrap as a row fills, so a sheet's
     fifth action never shrinks into a clipped label, and a button alone on
     the last line stays a button rather than a bar. */
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  width: 100%;
  margin: 0;
}
.drcy-artifact-actions > .button {
  flex: 0 0 auto;
  margin: 0 !important;
  padding-right: 0.5rem !important;
  padding-left: 0.5rem !important;
  white-space: nowrap;
}
.drcy-artifact-preview {
  flex: 1 1 auto;
  min-height: 10rem;
  max-height: 60vh;
  overflow: auto;
  margin: 0.75rem 0 0;
  padding: 0.75rem;
  background: var(--color-code-bg);
  border: 1px solid var(--color-secondary);
  border-radius: var(--border-radius);
  white-space: pre-wrap;
}
.drcy-change-summary {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 2fr);
  gap: 0.4rem 0.75rem;
  margin: 0;
}
.drcy-change-preview-heading {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.4rem;
}
.drcy-change-preview-heading a {
  flex: 0 0 auto;
  font-size: 0.85rem;
}
.drcy-change-preview {
  display: flex;
  flex: 0 0 16rem;
  align-items: center;
  justify-content: center;
  min-height: 10rem;
  margin-bottom: 0.75rem;
  overflow: auto;
  background: var(--color-white);
  border: 1px solid var(--color-secondary);
  border-radius: var(--border-radius);
}
.drcy-change-preview img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.drcy-change-summary dt {
  min-width: 0;
  overflow-wrap: anywhere;
  font-weight: var(--font-weight-semibold);
}
.drcy-change-summary dd {
  min-width: 0;
  margin: 0;
  overflow-wrap: anywhere;
}
@container (max-width: 28rem) {
  .drcy-change-summary {
    grid-template-columns: minmax(0, 1fr);
    row-gap: 0.25rem;
  }
  .drcy-change-summary dd {
    margin-bottom: 0.75rem;
  }
}
.drcy-change-validation {
  margin: 0.75rem 0 !important;
}
.drcy-change-validation ul {
  margin: 0.4rem 0 0;
  padding-left: 1.25rem;
}
.drcy-change-actions {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.5rem;
  margin-top: auto;
}
.drcy-change-actions > .button {
  min-width: 0;
  margin: 0 !important;
}
.drcy-file-row {
  cursor: default;
}
.drcy-file-row > .button {
  flex: 0 0 auto;
  margin: 0 !important;
}
/* A file row is not the expanding kind of list item, so cap the preview's
   height instead of letting it grow to the whole sheet. */
.drcy-file-preview {
  --drcy-csv-max-height: 24rem;
}
.drcy-artifact-csv {
  margin-top: 0.75rem;
}
.drcy-chat-artifact-card {
  --drcy-csv-max-height: 20rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.75rem;
  padding: 0.6rem 0.75rem 0.75rem;
  background: var(--color-box-body);
  border: 1px solid var(--color-secondary);
  border-radius: var(--border-radius);
}
.drcy-chat-artifact-card-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
}
.drcy-chat-artifact-card-header > .button {
  flex: 0 0 auto;
  margin: 0 !important;
}
.drcy-chat-artifact-card-footer {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
}
.drcy-chat-artifact-card-footer > .button {
  margin: 0 !important;
}
.drcy-chat-artifact-card-footer > [role="alert"] {
  flex: 1 1 100%;
}
.drcy-sheet-workspace-copy {
  flex: 1 1 16rem;
}
.drcy-sheet-workspace-title {
  font-size: 1.15rem;
  font-weight: var(--font-weight-semibold);
}
.drcy-file-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin: 0.5rem 0 0;
  padding: 0;
  list-style: none;
}
.drcy-file-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  max-width: 18rem;
  padding: 0.15rem 0.3rem 0.15rem 0.6rem;
  font-size: 0.85em;
  background: var(--color-secondary-alpha-20);
  border: 1px solid var(--color-secondary);
  border-radius: 1rem;
}
.drcy-file-chip a {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.drcy-file-chip-remove {
  padding: 0 0.3rem;
  color: var(--color-text-light-2);
  line-height: 1;
  background: none;
  border: 0;
  cursor: pointer;
}
.drcy-file-chip-remove:hover {
  color: var(--color-red);
}
.drcy-artifact-editor {
  flex: 1 1 auto;
  min-height: 16rem;
  width: 100%;
  resize: none;
  font-family: var(--fonts-monospace);
}
/* The form keeps its content height (no min-height: 0): the editor inside has a
   floor, and shrinking past it would paint the table over the Save row. The
   detail pane scrolls instead, and the table's viewport is capped so the
   content height stays bounded and its rows stay virtualized. */
.drcy-artifact-edit-form,
.drcy-artifact-content-field {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
}
.drcy-artifact-edit-form {
  --drcy-csv-max-height: 32rem;
}
.drcy-artifact-drawer-enter-active,
.drcy-artifact-drawer-leave-active {
  transition: opacity 200ms ease, transform 200ms ease;
}
.drcy-artifact-drawer-enter-from,
.drcy-artifact-drawer-leave-to {
  opacity: 0;
  transform: translateX(1rem);
}
</style>

<!-- Unscoped: print isolation, plus the repo-level branch picker which lives in
     the page template (outside this component), so it can't be scoped. -->
<style> /* eslint-disable-line vue-scoped-css/enforce-style-type */
/* eslint-disable vue-scoped-css/no-unused-selector */

.drcy-chat-workspace {
  display: flex;
  overflow: clip;
  align-items: flex-start;
  margin: 1rem 0;
}
.drcy-chat-main {
  flex: 1 1 auto;
  min-width: 0;
  margin: 0 !important;
}
.drcy-chat-compose-area.drcy-chat-dragover .ui.segment {
  outline: 2px dashed var(--color-primary);
  outline-offset: 2px;
}
.drcy-chat-upload-error:empty {
  display: none;
}
.drcy-chat-artifact-sidebar-target {
  position: sticky;
  top: 1rem;
  flex: 0 0 0;
  min-width: 0;
  overflow: hidden;
  opacity: 0;
  transition: flex-basis 200ms ease, opacity 160ms ease;
}
.drcy-chat-artifact-sidebar-target:has(> .drcy-artifact-sidebar) {
  flex-basis: clamp(25rem, 38vw, 32rem);
  margin-left: 1rem;
  opacity: 1;
}

@media (max-width: 959.98px) {
  .drcy-chat-artifact-sidebar-target:has(> .drcy-artifact-sidebar) {
    position: fixed;
    z-index: 1001;
    inset: 0 0 0 auto;
    width: min(32rem, 100vw);
    margin-left: 0;
    box-shadow: -8px 0 24px var(--color-shadow);
  }
  .drcy-artifact-sidebar {
    position: static;
    width: 100%;
    height: 100%;
    min-height: 0;
    border-radius: 0 !important;
  }
}

@media (prefers-reduced-motion: reduce) {
  .drcy-chat-artifact-sidebar-target,
  .drcy-artifact-drawer-enter-active,
  .drcy-artifact-drawer-leave-active {
    transition: none;
  }
}

@media print {
  body * {
    visibility: hidden;
  }
  .drcy-chat-panel,
  .drcy-chat-panel * {
    visibility: visible;
  }
  .drcy-chat-panel {
    position: absolute;
    inset: 0;
    margin: 0;
    border: none;
    box-shadow: none;
  }
  .drcy-chat-toolbar,
  .drcy-chat-end,
  .drcy-chat-compose-area,
  .drcy-chat-artifact-sidebar-target {
    display: none !important;
  }
  .drcy-chat-messages {
    max-height: none !important;
    overflow: visible !important;
  }
}
</style>
